<template>
   <!-- Display a single block component item from the available blockComponent list. -->
   <div v-if="block.id !== ''" class="flex size-full flex-col justify-center py-4">
      <div
         class="shrink px-1 pt-2 pb-1"
         :class="{ 'overflow-y-auto': !state.overhangingBlocks.includes(props.block.componentName) }"
      >
         <!-- Heading, subtitle -->
         <div class="mx-auto pb-4">
            <h2 v-if="props.block.heading" data-cy="block-heading" class="blockQuestionTitle">
               {{ props.block.heading }}
               <span v-if="!props.block.isRequired && !isInformational" class="text-base">
                  ({{ $t("validation.optional_input") }})</span
               >
            </h2>
            <RichTextRenderer
               v-if="props.block.subheading"
               :class="subheadingClass"
               data-cy="block-subheading"
               :document="props.block.subheading"
            />
         </div>

         <!-- Block components -->
         <!-- Almost all block components have the same set of props, except for a few that require additional ones -->

         <!-- Kit registration block has additional next-displayed event -->
         <KitRegistrationV1
            v-if="block.componentName === 'kit-registration-v1'"
            :block="block"
            :userBlockAnswer="userBlockAnswer"
            :showBlockErrors="state.showBlockErrors"
            @update-answer="(a) => (state.currentAnswer = a)"
            @update-valid="(v) => (state.isAnswerValid = v)"
            @next-displayed="(n) => (state.isNextButtonDisplayed = n)"
         />

         <!-- PublicParticipationClaimer has additional flow prop -->
         <PublicParticipationClaimerV1
            v-else-if="block.componentName === 'public-participation-claimer-v1'"
            :block="block"
            :flow="flow"
            :userBlockAnswer="userBlockAnswer"
            :showBlockErrors="state.showBlockErrors"
            @update-answer="(a) => (state.currentAnswer = a)"
            @update-valid="(v) => (state.isAnswerValid = v)"
         />

         <!-- All other blocks that share the same set of props -->
         <component
            v-else
            :is="blockComponents[block.componentName]"
            :block="block"
            :userBlockAnswer="userBlockAnswer"
            :showBlockErrors="state.showBlockErrors"
            @update-answer="(a) => (state.currentAnswer = a)"
            @update-valid="(v) => (state.isAnswerValid = v)"
         />
      </div>
      <div class="px-1 pb-1">
         <NavButtons
            :currentBlock="block"
            :isBackButtonDisplayed="isBackButtonDisplayed"
            :isNextButtonDisplayed="state.isNextButtonDisplayed"
            :nextBtnText="nextBtnText"
            :currentAnswer="state.currentAnswer"
            :isAnswerValid="state.isAnswerValid"
            :nextCalledFromComponent="state.nextCalledFromComponent"
            @show-errors="state.showBlockErrors = true"
            @previous-click="$emit('previous-click')"
            @participation-reset="$emit('participation-reset')"
            @answer-submitted="
               (response) => {
                  $emit('answer-submitted', response);
               }
            "
         />
      </div>
   </div>
</template>

<script setup lang="ts">
import type { AnswerResp } from "@/data/api-layer/answersApi";
import type { FlowData } from "@/data/api-layer/flowsApi";
import type { FlowselBlock } from "@/components/flowsel/flowsel.type";
import BooleanChoiceV1 from "./choice/BooleanChoice-v1.vue";
import FileUploadV1 from "./upload/FileUpload-v1.vue";
import FlowEnd from "./flowEnd/FlowEnd.vue";
import InformedConsentV1 from "./consent/InformedConsent-v1.vue";
import KitOrderingV1 from "./kit/KitOrdering-v1.vue";
import KitLinkOrderingV1 from "./kit/KitLinkOrdering-v1.vue";
import KitRegistrationV1 from "@/components/flowsel/kit/KitRegistration-v1.vue";
import KitLinkRegistrationV1 from "./kit/KitLinkRegistration-v1.vue";
import MultiChoiceV1 from "./choice/MultiChoice-v1.vue";
import ProfileAddressV1 from "./profile/ProfileAddress-v1.vue";
import ProfileDateOfBirthV1 from "./profile/ProfileDateOfBirth-v1.vue";
import ProfileNamesV1 from "./profile/ProfileNames-v1.vue";
import ProfilePhoneV1 from "./profile/ProfilePhone-v1.vue";
import ProfileSexV1 from "./profile/ProfileSex-v1.vue";
import ProfileRoleV1 from "./profile/ProfileRole-v1.vue";
import ProfileRepresentativeNameV1 from "./profile/ProfileRepresentativeName-v1.vue";
import RelativeDateV1 from "./date/RelativeDate-v1.vue";
import ShortTextV1 from "./text/ShortText-v1.vue";
import SingleChoiceV1 from "./choice/SingleChoice-v1.vue";
import SpecificConditionV1 from "./medical/SpecificCondition-v1.vue";
import RichTextV1 from "./information/RichText-v1.vue";
import EducationV1 from "./information/Education-v1.vue";
import DateRangeV1 from "./date/DateRange-v1.vue";
import DateV1 from "./date/Date-v1.vue";
import PublicFlowInvitationV1 from "./PublicFlowInvitation-v1.vue";
import AutocompleteSelectV1 from "./choice/AutocompleteSelect-v1.vue";
import ClaimCodeGeneratorV1 from "./claim/ClaimCodeGenerator-v1.vue";
import PublicParticipationClaimerV1 from "./claim/PublicParticipationClaimer-v1.vue";
import LongTextV1 from "./text/LongText-v1.vue";
// -- Components not ready for use
// import SingleChoiceWithInputV1 from "./choice/SingleChoiceWithInput-v1.vue";
// import AddressV1 from "./contact/Address-v1.vue";
// import PhoneV1 from "./contact/Phone-v1.vue";
// import NumberV1 from "./number/Number-v1.vue";
// import ProfileMedicalConditionsV1 from "./profile/ProfileMedicalConditions-v1.vue";
// import MedicalConditionsV1 from "./medical/MedicalConditions-v1.vue";
// import PhysicianDetailsV1 from "./medical/PhysicianDetails-v1.vue";

interface Props {
   block: FlowselBlock;
   userBlockAnswer?: AnswerResp;
   isBackButtonDisplayed: boolean;
   flow: FlowData;
}
interface States {
   currentAnswer?: AnswerResp;
   isAnswerValid: boolean;
   nextCalledFromComponent: number;
   showBlockErrors: boolean;
   overhangingBlocks: Array<string>;
   subtitleDetailBlocks: Array<string>;
   isNextButtonDisplayed: boolean;
}

// declarations
// -----------------------------------------
defineEmits(["answer-submitted", "previous-click", "participation-reset"]);
const props = defineProps<Props>();

// state
const state = reactive<States>({
   // This information will be emitted from each block
   currentAnswer: null,
   isAnswerValid: false,
   // If this is incremented by 1 each time 'next' is called from within a component rather than by a click of the 'next' button,
   // it can be easily passed to NavButtons and watched to trigger answer submission
   nextCalledFromComponent: 0,
   // Errors should only be shown once the user has pressed 'next'
   showBlockErrors: false,
   // Some block types need a different value for y-overflow - e.g. dropdowns should 'overhang' the block area when open
   overhangingBlocks: ["autocomplete-select-v1"],
   // Some block types are designed to hold more text in the question description
   subtitleDetailBlocks: ["kit-registration-v1"],
   // Some block types require hiding the next button until something has happened
   isNextButtonDisplayed: true,
});

const blockComponents = {
   "boolean-choice-v1": BooleanChoiceV1,
   "file-upload-v1": FileUploadV1,
   "flow-end": FlowEnd,
   "informed-consent-v1": InformedConsentV1,
   "kit-ordering-v1": KitOrderingV1,
   "kitlink-ordering-v1": KitLinkOrderingV1,
   "kitlink-registration-v1": KitLinkRegistrationV1,
   "multi-choice-v1": MultiChoiceV1,
   "profile-address-v1": ProfileAddressV1,
   "profile-date-of-birth-v1": ProfileDateOfBirthV1,
   "profile-names-v1": ProfileNamesV1,
   "profile-phone-v1": ProfilePhoneV1,
   "profile-sex-v1": ProfileSexV1,
   "profile-role-v1": ProfileRoleV1,
   "profile-representative-name-v1": ProfileRepresentativeNameV1,
   "relative-date-v1": RelativeDateV1, // Needs adding to contract.yml (if in use)
   "short-text-v1": ShortTextV1,
   "single-choice-v1": SingleChoiceV1,
   "specific-condition-v1": SpecificConditionV1,
   "education-v1": EducationV1,
   "rich-text-v1": RichTextV1,
   "date-range-v1": DateRangeV1,
   "date-v1": DateV1,
   "public-flow-invitation-v1": PublicFlowInvitationV1,
   "autocomplete-select-v1": AutocompleteSelectV1,
   "claim-code-generator-v1": ClaimCodeGeneratorV1,
   "long-text-v1": LongTextV1,
   // "address-v1": AddressV1,
   // "medical-conditions-v1": MedicalConditionsV1,
   // "medical-physician-details-v1": PhysicianDetailsV1,
   // "number-v1": NumberV1,
   // "phone-v1": PhoneV1,
   // "profile-medical-conditions-v1": ProfileMedicalConditionsV1,
   // "single-choice-with-input-v1": SingleChoiceWithInputV1, // Not ready for use
};

// Computed
// -----------------------------------------
// Some components have custom next button text if the user hasn't submitted their answer yet
const nextBtnText = computed(() => {
   if (props.block?.componentName === "kit-ordering-v1" && !props.userBlockAnswer?.metadata?.orderId) {
      return "confirm_kit_order";
   } else if (props.block?.componentName === "kit-ordering-v2" && !props.userBlockAnswer?.metadata?.orderId) {
      return "confirm_kit_order";
   } else if (props.block?.componentName === "informed-consent-v1" && !props.userBlockAnswer?.data) {
      return "consent";
   } else if (props.block?.componentName === "kit-registration-v1" && !props.userBlockAnswer?.metadata?.registeredAt) {
      return "submit";
   }
   return "default";
});

const isInformational = computed(() => {
   // Blocks which can only be 'optional' - there is no input [EducationV1, RichTextV1]
   const informationalBloksNames = ["education-v1", "rich-text-v1"];
   // Don't show the 'optional' label in this case
   return informationalBloksNames.includes(props.block?.componentName);
});

const subheadingClass = computed(() => {
   return state.subtitleDetailBlocks.includes(props.block.componentName)
      ? `blockQuestionSubtitleDetail`
      : `blockQuestionSubtitle`;
});
</script>
